// frontend/src/context/ChurchContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';

// Define the shape of the Church object as it comes from the backend
export interface Church {
    id: string;
    name: string;
    identifier: string;
    branding: {
        primaryColor: string;
        logoUrl: string;
    };
    features: {
        registrationForm: boolean;
        memberManagement: boolean;
        // Add more features as they are defined in your Church model
    };
}

// Define the shape of the context value
interface ChurchContextType {
    currentChurch: Church | null;
    loading: boolean;
    error: string | null;
    fetchChurchDetails: (identifier: string) => Promise<void>;
}

// Create the context with a default null value
const ChurchContext = createContext<ChurchContextType | undefined>(undefined);

// Define props for the ChurchProvider
interface ChurchProviderProps {
    children: ReactNode;
    initialChurchIdentifier?: string; // Optional: for initial load
}

export const ChurchProvider: React.FC<ChurchProviderProps> = ({ children, initialChurchIdentifier }) => {
    const [currentChurch, setCurrentChurch] = useState<Church | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchChurchDetails = async (identifier: string) => {
        setLoading(true);
        setError(null);
        try {
              const response = await axios.get<Church>(`${import.meta.env.VITE_API_BASE_URL}/churches/${import.meta.env.VITE_APP_CHURCH_IDENTIFIER}`, {
                                                    headers: {
                                                    'X-Church-Identifier': import.meta.env.VITE_APP_CHURCH_IDENTIFIER
                                                    }
            });
            setCurrentChurch(response.data);
            // Optional: Store in session storage for basic persistence
            sessionStorage.setItem('currentChurch', JSON.stringify(response.data));
        } catch (err: any) {
          console.error('Failed to fetch church details:', err);
            setError(err.response?.data?.message || 'Could not load church details.');
            setCurrentChurch(null);
            sessionStorage.removeItem('currentChurch'); // Clear if error
        } finally {
            setLoading(false);
        }
    };

    // Effect to try to load from session storage or fetch on initial render
    useEffect(() => {
        if (initialChurchIdentifier) {
            const storedChurch = sessionStorage.getItem('currentChurch');
            if (storedChurch) {
                try {
                    const parsedChurch: Church = JSON.parse(storedChurch);
                    // Only use stored if it matches the current identifier
                    if (parsedChurch.identifier === initialChurchIdentifier) {
                        setCurrentChurch(parsedChurch);
                        setLoading(false);
                        return; // Exit if loaded from session storage
                    }
                } catch (e) {
                    console.error("Failed to parse stored church details:", e);
                    sessionStorage.removeItem('currentChurch');
                }
            }
            // If not found in session storage or identifier mismatch, fetch
            fetchChurchDetails(initialChurchIdentifier);
        } else {
            setLoading(false); // No initial identifier provided
        }
    }, [initialChurchIdentifier]); // Re-run if initialIdentifier changes (unlikely in this setup)

    return (
        <ChurchContext.Provider value={{ currentChurch, loading, error, fetchChurchDetails }}>
            {children}
        </ChurchContext.Provider>
    );
};

// Custom hook to use the Church Context
export const useChurch = () => {
    const context = useContext(ChurchContext);
    if (context === undefined) {
        throw new Error('useChurch must be used within a ChurchProvider');
    }
    return context;
};
